import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useApi, useApiMutation } from '../hooks/useApi'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { DeploymentConfigWizard } from '../components/DeploymentConfigWizard'
import { formatRelativeTime, getStatusIcon, formatBytes } from '../lib/utils'
import type { Customer, Deployment, CustomerResource, CustomerConfig, FeatureFlag } from '../types'

interface CustomerDetailData {
  customer: Customer
  deployments: Deployment[]
  resources: CustomerResource[]
  configs: CustomerConfig[]
  feature_flags: FeatureFlag[]
}

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState<'overview' | 'deployments' | 'config' | 'resources'>('overview')
  const [editingConfig, setEditingConfig] = useState<string | null>(null)
  const [configValue, setConfigValue] = useState('')
  const [isDeploymentWizardOpen, setIsDeploymentWizardOpen] = useState(false)

  // Fetch customer data
  const { data: customer, loading, error, refetch } = useApi<Customer>(`/customers/${id}`)
  
  // Fetch provisioning status
  const { data: provisioningStatus, loading: provisioningStatusLoading, refetch: refetchProvisioningStatus } = useApi<{
    status: 'not_started' | 'in_progress' | 'completed' | 'failed'
    deployment?: Deployment
    resources: CustomerResource[]
  }>(`/customers/${id}/provisioning-status`)

  // Mutations
  const { mutate: updateConfig, loading: updatingConfig } = useApiMutation()
  const { mutate: toggleFeatureFlag, loading: togglingFlag } = useApiMutation()
  const { mutate: createDeployment, loading: creatingDeployment } = useApiMutation()
  const { mutate: provisionResources, loading: provisioning } = useApiMutation()

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !customer) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading customer
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error || 'Customer not found'}
            </div>
            <div className="mt-4">
              <Link to="/customers">
                <Button variant="outline" size="sm">
                  Back to Customers
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // For MVP, we only have customer data. Deployments, resources, etc. would be separate API calls
  const deployments: Deployment[] = []
  const resources: CustomerResource[] = []
  const configs: CustomerConfig[] = []
  const feature_flags: FeatureFlag[] = []

  const handleConfigSave = async (configKey: string) => {
    try {
      await updateConfig(`/customers/${id}/config`, {
        config_key: configKey,
        config_value: configValue,
      })
      setEditingConfig(null)
      setConfigValue('')
      refetch()
    } catch (error) {
      console.error('Failed to update config:', error)
    }
  }

  const handleFeatureFlagToggle = async (flagName: string, enabled: boolean) => {
    try {
      await toggleFeatureFlag(`/customers/${id}/feature-flags`, {
        feature_name: flagName,
        enabled: !enabled,
      })
      refetch()
    } catch (error) {
      console.error('Failed to toggle feature flag:', error)
    }
  }

  const handleNewDeployment = async () => {
    try {
      await createDeployment(`/deployments`, {
        customer_id: id!,
        version: 'latest',
      })
      refetch()
    } catch (error) {
      console.error('Failed to create deployment:', error)
    }
  }

  const handleProvisionResources = () => {
    setIsDeploymentWizardOpen(true)
  }

  const handleDeploymentSuccess = () => {
    refetch()
    refetchProvisioningStatus()
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'deployments', label: 'Deployments', icon: '🚀' },
    { id: 'config', label: 'Configuration', icon: '⚙️' },
    { id: 'resources', label: 'Resources', icon: '🔧' },
  ] as const

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Link to="/customers">
            <Button variant="ghost" size="sm">
              ← Back to Customers
            </Button>
          </Link>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-medium text-lg">
                {customer.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{customer.name}</h1>
              <div className="flex items-center space-x-4 mt-1">
                <Badge 
                  variant={customer.status === 'active' ? 'success' : 'default'}
                >
                  {getStatusIcon(customer.status)} {customer.status}
                </Badge>
                <Badge variant="info" className="uppercase">
                  {customer.region}
                </Badge>
                {customer.domain && (
                  <a 
                    href={`https://${customer.domain}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    {customer.domain} ↗
                  </a>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {provisioningStatus?.status === 'not_started' ? (
              <Button 
                onClick={handleProvisionResources}
                className="bg-green-600 hover:bg-green-700"
              >
                Deploy Infrastructure
              </Button>
            ) : provisioningStatus?.status === 'in_progress' ? (
              <Button 
                disabled
                loading={true}
                className="bg-yellow-600"
              >
                Provisioning...
              </Button>
            ) : provisioningStatus?.status === 'completed' ? (
              <Button 
                onClick={handleNewDeployment}
                loading={creatingDeployment}
              >
                New Deployment
              </Button>
            ) : provisioningStatus?.status === 'failed' ? (
              <Button 
                onClick={handleProvisionResources}
                variant="destructive"
              >
                Retry Deployment
              </Button>
            ) : (
              <Button 
                disabled
                loading={provisioningStatusLoading}
              >
                Loading...
              </Button>
            )}
            <Button variant="outline">
              Edit Customer
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer Info */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Name</label>
                    <p className="text-sm text-gray-900">{customer.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <p className="text-sm text-gray-900">{customer.status}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Region</label>
                    <p className="text-sm text-gray-900">{customer.region.toUpperCase()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created</label>
                    <p className="text-sm text-gray-900">{formatRelativeTime(customer.created_at)}</p>
                  </div>
                  {customer.domain && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Domain</label>
                      <p className="text-sm text-gray-900">{customer.domain}</p>
                    </div>
                  )}
                  {customer.contact_email && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Contact Email</label>
                      <p className="text-sm text-gray-900">{customer.contact_email}</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total Deployments</span>
                    <span className="text-sm font-medium">{deployments.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Active Resources</span>
                    <span className="text-sm font-medium">{resources.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Feature Flags</span>
                    <span className="text-sm font-medium">
                      {feature_flags.filter(f => f.enabled).length}/{feature_flags.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Last Deployment</span>
                    <span className="text-sm font-medium">
                      {deployments.length > 0 
                        ? formatRelativeTime(deployments[0].started_at)
                        : 'Never'
                      }
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Provisioning Status */}
            <Card>
              <CardHeader>
                <CardTitle>Provisioning Status</CardTitle>
              </CardHeader>
              <CardContent>
                {provisioningStatusLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : provisioningStatus ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Status</span>
                      <Badge 
                        variant={
                          provisioningStatus.status === 'completed' ? 'success' :
                          provisioningStatus.status === 'in_progress' ? 'info' :
                          provisioningStatus.status === 'failed' ? 'destructive' : 'default'
                        }
                      >
                        {provisioningStatus.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600">Resources</span>
                      <span className="text-sm font-medium">{provisioningStatus.resources.length}</span>
                    </div>
                    {provisioningStatus.deployment && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Last Provisioning</span>
                        <span className="text-sm font-medium">
                          {formatRelativeTime(provisioningStatus.deployment.started_at)}
                        </span>
                      </div>
                    )}
                    {provisioningStatus.resources.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-500 mb-2">Provisioned Resources:</p>
                        <div className="space-y-1">
                          {provisioningStatus.resources.map((resource) => (
                            <div key={resource.id} className="flex justify-between text-xs">
                              <span className="text-gray-600">{resource.resource_type.toUpperCase()}</span>
                              <span className="text-gray-900">{resource.resource_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Unable to load provisioning status</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'deployments' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Deployment History</CardTitle>
              <Button 
                onClick={handleNewDeployment}
                loading={creatingDeployment}
                size="sm"
              >
                New Deployment
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {deployments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Version
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Started
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deployments.map((deployment) => (
                      <tr key={deployment.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {deployment.version}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge 
                            variant={
                              deployment.status === 'completed' ? 'success' :
                              deployment.status === 'failed' ? 'error' :
                              deployment.status === 'in_progress' ? 'info' : 'warning'
                            }
                          >
                            {getStatusIcon(deployment.status)} {deployment.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatRelativeTime(deployment.started_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {deployment.completed_at 
                            ? `${Math.round((new Date(deployment.completed_at).getTime() - new Date(deployment.started_at).getTime()) / 1000)}s`
                            : deployment.status === 'in_progress' ? 'Running...' : '-'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Link to={`/deployments/${deployment.id}`}>
                            <Button variant="outline" size="sm">
                              View Details
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🚀</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No deployments yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Create your first deployment to get started.
                </p>
                <Button onClick={handleNewDeployment} loading={creatingDeployment}>
                  Create First Deployment
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'config' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration Values */}
          <Card>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {configs.map((config) => (
                  <div key={config.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {config.config_key}
                      </div>
                      {editingConfig === config.config_key ? (
                        <div className="mt-2 flex items-center space-x-2">
                          <Input
                            value={configValue}
                            onChange={(e) => setConfigValue(e.target.value)}
                            placeholder={config.is_secret ? "Enter new value" : config.config_value || "Enter value"}
                            type={config.is_secret ? "password" : "text"}
                            className="flex-1"
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleConfigSave(config.config_key)}
                            loading={updatingConfig}
                          >
                            Save
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setEditingConfig(null)
                              setConfigValue('')
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 mt-1">
                          {config.is_secret ? '••••••••' : config.config_value || 'Not set'}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {config.is_secret && (
                        <Badge variant="warning" className="text-xs">Secret</Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingConfig(config.config_key)
                          setConfigValue(config.is_secret ? '' : config.config_value || '')
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ))}
                
                {configs.length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-500">No configuration values set</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Feature Flags */}
          <Card>
            <CardHeader>
              <CardTitle>Feature Flags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {feature_flags.map((flag) => (
                  <div key={flag.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {flag.feature_name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Updated {formatRelativeTime(flag.updated_at)}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={flag.enabled ? 'success' : 'default'}>
                        {flag.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleFeatureFlagToggle(flag.feature_name, flag.enabled)}
                        loading={togglingFlag}
                      >
                        Toggle
                      </Button>
                    </div>
                  </div>
                ))}
                
                {feature_flags.length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-500">No feature flags configured</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'resources' && (
        <Card>
          <CardHeader>
            <CardTitle>Cloudflare Resources</CardTitle>
          </CardHeader>
          <CardContent>
            {resources.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {resources.map((resource) => (
                  <div key={resource.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="info" className="uppercase">
                        {resource.resource_type}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {resource.region && resource.region.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {resource.resource_name}
                    </div>
                    <div className="text-xs text-gray-500 mb-2">
                      ID: {resource.resource_id}
                    </div>
                    <div className="text-xs text-gray-500">
                      Created {formatRelativeTime(resource.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🔧</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No resources found
                </h3>
                <p className="text-gray-500">
                  Resources will appear here after the first deployment.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Deployment Configuration Wizard */}
      <DeploymentConfigWizard
        isOpen={isDeploymentWizardOpen}
        onClose={() => setIsDeploymentWizardOpen(false)}
        onSuccess={handleDeploymentSuccess}
        customer={customer}
      />
    </div>
  )
}